import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersContactFormData,
  ParametersContactSchema,
} from "@/types/forms/bo/clientsParameters.form";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";

type UpdateContactModalProps = {
  closeModal: () => void;
  contactEmail: string;
  contactPhoneNumber: string;
  title: string;
};

export const UpdateContactModal = ({
  closeModal,
  contactEmail,
  contactPhoneNumber,
  title,
}: UpdateContactModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const parametersContactForm = useForm<ParametersContactFormData>({
    resolver: zodResolver(ParametersContactSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      contactEmail,
      contactPhoneNumber,
    },
  });

  const onSubmit: SubmitHandler<ParametersContactFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="contactForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("contactModal.submit")}
        </Button>
      }
    >
      <form
        id="contactForm"
        onSubmit={parametersContactForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledUnifiedInput
          control={parametersContactForm.control}
          name="contactEmail"
          label={t("contactModal.emailLabel")}
          defaultValue={contactEmail}
          inputType="text"
        />
        <StyledPhoneInput
          control={parametersContactForm.control}
          name="contactPhoneNumber"
          title={t("contactModal.phoneLabel")}
          defaultCountry="FR"
          className="w-full"
        />
      </form>
    </Modal>
  );
};
